import { createSlice } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import {
  activateAccount,
  changePassword,
  forgotPassword,
  login,
  oauthLogin,
  refreshUser,
  register,
  resetPassword,
  updateProfile,
} from '../actions/auth.action';
import { parseJson } from '../../utils/string/parseJson';
import { compactPermissions } from '../../utils/permissions';

// User lưu vào store và cookie (giới hạn ~4KB): giao diện chỉ dùng `access`, nên khi có `access` thì bỏ
// permissions dạng thô (super admin có 38 quyền); payload cũ chưa có `access` thì giữ permissions rút gọn
const toStoredUser = user => {
  if (!user) return user;
  const { permissions, ...rest } = user;
  return rest.access
    ? rest
    : { ...rest, permissions: compactPermissions(permissions) };
};

const getUserFromCookie = () => {
  const user = Cookies.get('user');
  if (user) {
    try {
      return parseJson(user);
    } catch {
      return null;
    }
  }
  return null;
};
const initialState = {
  isLoading: false,
  isNeedReloadData: false,
  accessToken: Cookies.get('access_token'),
  user: getUserFromCookie(),
};

export const authReducer = createSlice({
  name: 'Auth',
  initialState,
  reducers: {
    clearToken: state => {
      state.accessToken = null;
      state.user = null;

      Cookies.remove('access_token');
      Cookies.remove('user');

      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(login.pending, state => {
        state.isLoading = true;
        state.isNeedReloadData = false;
      })
      .addCase(login.fulfilled, (state, action) => {
        if (action.payload?.accessToken && action.payload?.user) {
          const { accessToken, user } = action.payload;
          state.accessToken = accessToken;
          state.user = toStoredUser(user);
          state.isLoading = false;
          Cookies.set('access_token', accessToken);
          Cookies.set('user', JSON.stringify(state.user));
        }
      })
      .addCase(login.rejected, state => {
        state.isLoading = false;
      })
      .addCase(register.pending, state => {
        state.isLoading = true;
        state.isNeedReloadData = false;
      })
      .addCase(register.fulfilled, (state, action) => {
        if (action.payload) {
          state.isLoading = false;
        }
      })
      .addCase(register.rejected, state => {
        state.isLoading = false;
      })
      .addCase(forgotPassword.pending, state => {
        state.isLoading = true;
        state.isNeedReloadData = false;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        if (action.payload?.data) {
          state.isLoading = false;
        }
      })
      .addCase(forgotPassword.rejected, state => {
        state.isLoading = false;
      })
      .addCase(resetPassword.pending, state => {
        state.isLoading = true;
        state.isNeedReloadData = false;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        if (action.payload?.data) {
          state.isLoading = false;
        }
      })
      .addCase(resetPassword.rejected, state => {
        state.isLoading = false;
      })
      .addCase(activateAccount.pending, state => {
        state.isLoading = true;
        state.isNeedReloadData = false;
      })
      .addCase(activateAccount.fulfilled, (state, action) => {
        if (action.payload?.data) {
          state.isLoading = false;
        }
      })
      .addCase(activateAccount.rejected, state => {
        state.isLoading = false;
      })
      .addCase(oauthLogin.pending, state => {
        state.isLoading = true;
        state.isNeedReloadData = false;
      })
      .addCase(oauthLogin.fulfilled, (state, action) => {
        if (action.payload?.accessToken && action.payload?.user) {
          const { accessToken, user } = action.payload;
          state.accessToken = accessToken;
          state.user = toStoredUser(user);
          state.isLoading = false;
          Cookies.set('access_token', accessToken);
          Cookies.set('user', JSON.stringify(state.user));
          state.isLoading = false;
        }
      })
      .addCase(oauthLogin.rejected, state => {
        state.isLoading = false;
      })
      .addCase(changePassword.pending, state => {
        state.isLoading = true;
        state.isNeedReloadData = false;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        if (action.payload?.data) {
          state.isLoading = false;
        }
      })
      .addCase(changePassword.rejected, state => {
        state.isLoading = false;
      })
      .addCase(updateProfile.pending, state => {
        state.isLoading = true;
        state.isNeedReloadData = false;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        if (action.payload?.data) {
          state.isLoading = false;
          state.user = toStoredUser(action.payload.data);
          Cookies.set('user', JSON.stringify(state.user));
        }
      })
      .addCase(updateProfile.rejected, state => {
        state.isLoading = false;
      })
      // Làm mới quyền: detail không trả token nên giữ token đang có
      .addCase(refreshUser.fulfilled, (state, action) => {
        if (action.payload?.user) {
          state.user = toStoredUser({
            ...state.user,
            ...action.payload.user,
            token: state.user?.token ?? action.payload.user.token,
          });
          Cookies.set('user', JSON.stringify(state.user));
        }
      });
  },
});

export const { setToken, clearToken, setIsLoadingAuth } = authReducer.actions;

export default authReducer.reducer;
